export const userManagementRoles = [
  'systemAdministrator',
  'generalAdministrator',
  'generalProfessional',
  'publicSafetyAdministrator',
  'publicSafetyAgent',
  'healthAdministrator',
  'healthAgent',
  'agricultureAdministrator',
  'agricultureProfessional',
] as const

export type UserManagementRole = (typeof userManagementRoles)[number]

const manageableRolesByRole: Record<UserManagementRole, readonly UserManagementRole[]> = {
  systemAdministrator: userManagementRoles,
  generalAdministrator: [
    'generalProfessional',
    'publicSafetyAdministrator',
    'publicSafetyAgent',
    'healthAdministrator',
    'healthAgent',
    'agricultureAdministrator',
    'agricultureProfessional',
  ],
  generalProfessional: [],
  publicSafetyAdministrator: ['publicSafetyAdministrator', 'publicSafetyAgent'],
  publicSafetyAgent: [],
  healthAdministrator: ['healthAdministrator', 'healthAgent'],
  healthAgent: [],
  agricultureAdministrator: ['agricultureAdministrator', 'agricultureProfessional'],
  agricultureProfessional: [],
}

export function isUserManagementRole(role: unknown): role is UserManagementRole {
  return typeof role === 'string' && userManagementRoles.includes(role as UserManagementRole)
}

export function getManageableRolesForRole(role: unknown): UserManagementRole[] {
  if (!isUserManagementRole(role)) {
    return []
  }

  return [...manageableRolesByRole[role]]
}

export function canListUsers(actorRole: unknown): boolean {
  return getManageableRolesForRole(actorRole).length > 0
}

export function canCreateUserWithRole(actorRole: unknown, targetRole: unknown): boolean {
  if (!isUserManagementRole(targetRole)) {
    return false
  }

  return getManageableRolesForRole(actorRole).includes(targetRole)
}

export function canFetchUser({
  actorRole,
  actorUserId,
  targetUserId,
  targetRole,
}: {
  actorRole: unknown
  actorUserId: string
  targetUserId: string
  targetRole: unknown
}): boolean {
  if (actorUserId === targetUserId) {
    return true
  }

  return canDeleteUser(actorRole, targetRole)
}

export function canUpdateUserRole(
  actorRole: unknown,
  currentTargetRole: unknown,
  nextTargetRole: unknown,
): boolean {
  if (!isUserManagementRole(currentTargetRole) || !isUserManagementRole(nextTargetRole)) {
    return false
  }

  const manageableRoles = getManageableRolesForRole(actorRole)

  return manageableRoles.includes(currentTargetRole) && manageableRoles.includes(nextTargetRole)
}

export function canDeleteUser(actorRole: unknown, targetRole: unknown): boolean {
  if (!isUserManagementRole(targetRole)) {
    return false
  }

  return getManageableRolesForRole(actorRole).includes(targetRole)
}
